// src/pages/personal/analytics/FunnelSection/components/FunnelFilters.tsx
import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Filter, Check, Loader, ChevronDown } from 'lucide-react';
import { Flex } from '@/components/ui/Layout';
import { COMPONENT_STYLES } from '@/theme';

interface FunnelFiltersProps {
  channels: string[];
  onChange: (channels: string[]) => void;
  disabled?: boolean;
  isLoading?: boolean;
  onReset?: () => void;
}

interface Channel {
  value: string;
  label: string;
  icon?: React.ReactNode;
}

/**
 * Компонент выбора каналов для воронки продаж
 * Адаптивный: на десктопе - выпадающее меню, на мобильных - раскрывающаяся карточка
 */
const FunnelFilters: React.FC<FunnelFiltersProps> = ({
  channels = [],
  onChange,
  disabled = false,
  isLoading = false,
  onReset
}) => {
  const { t } = useTranslation(['funnel', 'common']);
  const DEBUG = process.env.NODE_ENV === 'development';
  
  // Состояния компонента
  const [selectedChannels, setSelectedChannels] = useState<string[]>(channels);
  const [isApplying, setIsApplying] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  
  // Реф для отслеживания кликов вне компонента (только для десктопа)
  const filterRef = useRef<HTMLDivElement>(null);
  
  // Определяем, это мобильное устройство или нет
  const [isMobile, setIsMobile] = useState(false);
  
  // Проверка размера экрана при монтировании и при изменении размера
  useEffect(() => {
    const checkScreenSize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    // Проверяем размер экрана при монтировании
    checkScreenSize();
    
    // Добавляем слушатель изменения размера окна
    window.addEventListener('resize', checkScreenSize);
    
    // Удаляем слушатель при размонтировании
    return () => {
      window.removeEventListener('resize', checkScreenSize);
    };
  }, []);
  
  // Обновляем локальные каналы при изменении внешних
  useEffect(() => {
    if (!isApplying) {
      setSelectedChannels(channels);
    }
  }, [channels, isApplying]);
  
  // Обработчик кликов вне компонента для закрытия фильтра (только для десктопа)
  useEffect(() => {
    if (isMobile) return; // Пропускаем для мобильной версии
    
    const handleClickOutside = (event: MouseEvent) => {
      if (filterRef.current && !filterRef.current.contains(event.target as Node) && isOpen) {
        setIsOpen(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, isMobile]);
  
  // Все доступные каналы с улучшенным форматированием
  const allChannels: Channel[] = [
    { 
      value: 'instagram', 
      label: 'Instagram',
      icon: <span>📸</span>,
    },
    { 
      value: 'facebook', 
      label: 'Facebook',
      icon: <span>👍</span>,
    },
    { 
      value: 'seo', 
      label: 'SEO',
      icon: <span>🔍</span>,
    },
    { 
      value: 'maps', 
      label: 'Карты',
      icon: <span>📍</span>,
    },
    { 
      value: 'walk_in', 
      label: 'Визит в магазин',
      icon: <span>🏪</span>,
    },
    { 
      value: 'referral', 
      label: 'Реферал',
      icon: <span>👥</span>,
    },
    { 
      value: 'whatsapp', 
      label: 'WhatsApp',
      icon: <span>💬</span>,
    },
    { 
      value: 'other', 
      label: 'Другое',
      icon: <span>📋</span>,
    }
  ];
  
  // Обработчики
  const toggleChannel = (channelValue: string) => {
    setSelectedChannels(prev => 
      prev.includes(channelValue) 
        ? prev.filter(ch => ch !== channelValue) 
        : [...prev, channelValue]
    );
  };
  
  const applyFilters = () => {
    if (DEBUG) console.log('FunnelFilters - Применение фильтров:', selectedChannels);
    
    setIsApplying(true);
    
    try {
      // Передаем выбранные каналы в родительский компонент
      onChange(selectedChannels);
    } finally {
      // Добавляем задержку для визуального эффекта
      setTimeout(() => {
        setIsApplying(false);
        // Закрываем фильтр после применения только на десктопе
        if (!isMobile) {
          setIsOpen(false);
        }
      }, 300);
    }
  };
  
  const resetFilters = () => {
    if (DEBUG) console.log('FunnelFilters - Сброс фильтров');
    
    setSelectedChannels([]);
    
    if (onReset) {
      onReset();
    } else {
      onChange([]);
      // Закрываем фильтр после сброса только на десктопе
      if (!isMobile) {
        setIsOpen(false);
      }
    }
  };

  // Переключение состояния фильтра (открыт/закрыт)
  const toggleFilter = () => {
    setIsOpen(prev => !prev);
  };

  // Определяем, есть ли изменения в фильтрах
  const hasChanges = JSON.stringify(selectedChannels) !== JSON.stringify(channels);
  
  // Проверка активных фильтров
  const hasActiveFilters = selectedChannels.length > 0;
  
  // Состояние блокировки UI
  const isBlocked = disabled || isLoading || isApplying;
  
  // Стили для кнопок
  const buttonBaseClass = `${COMPONENT_STYLES?.button?.base || 'rounded-md font-medium focus:outline-none focus:ring-2 focus:ring-offset-2'} text-sm px-4 py-2`;

  // Рендер содержимого фильтра (каналы и кнопки)
  const renderFilterContent = () => (
    <>
      <div className="mb-2 font-medium">
        {t('filters.select_channels', 'Выберите каналы')}
      </div>

      {/* Горизонтальный список каналов */}
      <div className="flex flex-wrap gap-2 mb-4">
        {allChannels.map(channel => (
          <div 
            key={channel.value} 
            className={`flex items-center px-3 py-2 rounded-md cursor-pointer transition-colors border
            ${selectedChannels.includes(channel.value) 
              ? 'bg-[#245D33] text-white border-[#245D33]' 
              : 'bg-white border-gray-300 hover:bg-gray-50'
            } ${isBlocked ? 'opacity-60 cursor-not-allowed' : ''}`}
            onClick={() => !isBlocked && toggleChannel(channel.value)}
          >
            <div className="flex items-center">
              {channel.icon && <span className="mr-2">{channel.icon}</span>}
              <span>{channel.label}</span>
            </div>
            {selectedChannels.includes(channel.value) && (
              <Check size={16} className="ml-2" />
            )}
          </div>
        ))}
      </div>
      
      {/* Кнопки действий */}
      <Flex justify="between" gap="sm" className="mt-4">
        {/* Кнопка сброса */}
        {hasActiveFilters && (
          <button
            type="button"
            onClick={resetFilters}
            className={`${buttonBaseClass} ${COMPONENT_STYLES?.button?.secondary || 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'}`}
            disabled={isBlocked}
          >
            {t('filters.reset', 'Сбросить')}
          </button>
        )}
        
        {/* Кнопка применения фильтров */}
        <button
          type="button"
          onClick={applyFilters}
          className={`${buttonBaseClass} ${COMPONENT_STYLES?.button?.primary || 'bg-[#245D33] text-white hover:bg-[#1a4525]'} ${isApplying ? 'animate-pulse' : ''} ml-auto`}
          disabled={isBlocked || (!hasChanges && !isApplying)}
        >
          {isLoading || isApplying ? (
            <Flex align="center" gap="xs">
              <Loader className="animate-spin h-4 w-4" />
              {t('common:loading', 'Загрузка...')}
            </Flex>
          ) : (
            t('filters.apply', 'Применить')
          )}
        </button>
      </Flex>
    </>
  );
  
  // Кнопка-переключатель для фильтра
  const filterButton = (
    <button
      className={`bg-white border border-gray-300 flex items-center gap-2 px-3 py-2 rounded-md ${isBlocked ? 'opacity-60 cursor-not-allowed' : 'hover:bg-gray-50'} ${isOpen ? 'bg-gray-50' : ''}`}
      onClick={toggleFilter}
      disabled={isBlocked}
      type="button"
    >
      <Filter size={16} />
      <span>{t('filters.channels', 'Каналы')}</span>
      
      {/* Индикатор выбранных фильтров */}
      {hasActiveFilters && (
        <span className="bg-[#245D33] text-white rounded-full text-xs px-2 py-0.5 min-w-[20px] text-center">
          {selectedChannels.length}
        </span>
      )}
      
      <ChevronDown 
        size={16} 
        className={`transition-transform ${isOpen ? 'transform rotate-180' : ''}`} 
      />
    </button>
  );

  // Мобильная версия компонента
  if (isMobile) {
    return (
      <div className="w-full">
        {filterButton}
        
        {/* Раскрывающаяся карточка с фильтрами для мобильной версии */}
        {isOpen && (
          <div className="mt-2 bg-white border border-gray-200 rounded-md shadow-sm p-4">
            {renderFilterContent()}
          </div>
        )}
      </div>
    );
  }
  
  // Десктопная версия компонента
  return (
    <div className="relative" ref={filterRef}>
      {filterButton}
      
      {/* Выпадающая панель фильтров для десктопной версии */}
      {isOpen && (
        <div className="absolute left-0 mt-1 bg-white border border-gray-200 rounded-md shadow-lg z-10 p-4 min-w-[300px]">
          {renderFilterContent()}
        </div>
      )}
    </div>
  );
};

export default React.memo(FunnelFilters);